import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Books, CaretLeft, Trash, PencilSimple, ArrowUp, ArrowDown, X, BookOpen,
  FilePdf, FileImage, FileText, ShareNetwork, Copy, LinkSimple, Prohibit, Sparkle,
  Rows, SquaresFour,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { dmsApi, STATUS_STYLE, fmtDate, fmtSize } from './dmsApi';
import ShareMenu from './ShareMenu';
import AutoGroupDialog from './AutoGroupDialog';
import { DocFileViewer } from './DocFileViewer';

/** バインダー内の1件のサムネイル（遅延読み込み・使い終わったら破棄）。 */
const MemberThumb = ({ docId, mime, className = '', iconSize = 18 }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true; let made = null;
    setUrl(null);
    dmsApi.thumbUrl(docId).then((u) => {
      if (!u) return;
      if (alive) { made = u; setUrl(u); } else { URL.revokeObjectURL(u); }
    }).catch(() => {});
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [docId]);
  if (url) {
    return <img src={url} alt="" className={`object-cover object-top bg-white ${className}`}
      data-testid={`bundle-thumb-${docId}`} />;
  }
  return (
    <span className={`flex items-center justify-center bg-zinc-50 ${className}`}
      data-testid={`bundle-thumb-fallback-${docId}`}>{icon(mime, iconSize)}</span>
  );
};

const SORTS = [
  { key: 'title', label: '名前' },
  { key: 'document_number', label: '書類番号' },
  { key: 'updated_at', label: '更新日' },
  { key: 'doc_type_label', label: '種類' },
];

const icon = (mime, size = 18) => {
  if (mime === 'application/pdf') return <FilePdf size={size} weight="duotone" className="text-rose-500 shrink-0" />;
  if ((mime || '').startsWith('image/')) return <FileImage size={size} weight="duotone" className="text-sky-500 shrink-0" />;
  return <FileText size={size} weight="duotone" className="text-zinc-400 shrink-0" />;
};

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success('リンクをコピーしました'); }
  catch { window.prompt('コピーしてください:', text); }
};

export function CreateBundleDialog({ open, documentIds, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setName(''); }, [open]);
  const submit = async () => {
    if (!name.trim()) { toast.error('バインダー名を入力してください'); return; }
    setBusy(true);
    try {
      await dmsApi.createBundle({ name: name.trim(), document_ids: documentIds });
      toast.success(`バインダー「${name.trim()}」を作成しました（${documentIds.length}件）`);
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || '作成に失敗しました'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="bundle-create-dialog">
        <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><Books size={18} className="text-amber-600" /> 書類を束ねる</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-500">選択中の {documentIds?.length || 0}件 をバインダーに綴じます（書類の場所は変わりません）</p>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="バインダー名（例: SS-45BMC 提出書類一式）"
          className="h-9 text-sm" data-testid="bundle-name-input" autoFocus
          onKeyDown={(e) => e.key === 'Enter' && submit()} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="bundle-create-save">束ねる</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** 書類を選んでバインダーを作る — バインダー画面から直接作れるようにする。
 *  以前は「分割表示モードで選択」しか入口がなく、この画面では何も始められなかった。 */
export function PickDocumentsDialog({ open, onClose, onCreated }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState({});
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQ(''); setPicked({}); setName('');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const t = setTimeout(() => {
      dmsApi.documents(q.trim() ? { q: q.trim(), limit: 40 } : { limit: 40 })
        .then((r) => { if (alive) setRows(r.documents || r || []); })
        .catch(() => { if (alive) setRows([]); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [open, q]);

  const ids = Object.keys(picked).filter((k) => picked[k]);
  const submit = async () => {
    if (ids.length < 1) { toast.error('書類を選んでください'); return; }
    if (!name.trim()) { toast.error('バインダー名を入力してください'); return; }
    setBusy(true);
    try {
      await dmsApi.createBundle({ name: name.trim(), document_ids: ids });
      toast.success(`バインダー「${name.trim()}」を作成しました（${ids.length}件）`);
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || '作成に失敗しました'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="bundle-pick-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Books size={18} className="text-amber-600" /> バインダーを作成
          </DialogTitle>
        </DialogHeader>
        <Input value={name} onChange={(e) => setName(e.target.value)}
          placeholder="バインダー名（例: SS-45BMC 提出書類一式）"
          className="h-9 text-sm" data-testid="bundle-pick-name" autoFocus />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="書類を検索 — ファイル名・図番・内容"
          className="h-9 text-sm" data-testid="bundle-pick-search" />
        <div className="max-h-72 overflow-y-auto space-y-1 border border-zinc-200 rounded-md p-1.5">
          {rows.length === 0 && <p className="text-xs text-zinc-400 py-6 text-center">書類が見つかりません</p>}
          {rows.map((d) => (
            <label key={d.id} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-amber-50 cursor-pointer"
              data-testid={`bundle-pick-row-${d.id}`}>
              <input type="checkbox" checked={!!picked[d.id]} className="h-4 w-4 accent-amber-600"
                onChange={(e) => setPicked((p) => ({ ...p, [d.id]: e.target.checked }))} />
              {icon(d.mime_type, 15)}
              <span className="min-w-0 flex-1">
                <span className="block text-xs text-zinc-800 truncate">{d.title}</span>
                <span className="block text-[10px] text-zinc-400 font-mono">{d.document_number} · {fmtDate(d.updated_at)}</span>
              </span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <span className="mr-auto text-[11px] text-zinc-400">{ids.length}件 選択中</span>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700"
            data-testid="bundle-pick-save">束ねる</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BundlesView({ meta, onOpenDoc, refreshKey, initialBundleId }) {
  const [bundles, setBundles] = useState([]);
  const [detail, setDetail] = useState(null);   // {bundle, documents, share}
  const [renaming, setRenaming] = useState(null);
  const [extShareOpen, setExtShareOpen] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [layout, setLayout] = useState('list');   // list | grid
  const [sort, setSort] = useState(null);         // {key, dir}
  const [big, setBig] = useState(null);           // 原寸ビューアーで開いている書類

  const loadList = useCallback(() => {
    dmsApi.bundles().then(setBundles).catch(() => setBundles([]));
  }, []);
  useEffect(() => { loadList(); }, [loadList, refreshKey]);
  useEffect(() => {
    dmsApi.folders().then((f) => setFolders(f.filter((x) => !x.is_archive_folder))).catch(() => {});
  }, []);

  const openDetail = async (bid) => {
    try { setDetail(await dmsApi.bundle(bid)); }
    catch (e) { toast.error(e?.response?.data?.detail || 'バインダーの取得に失敗しました'); }
  };
  // detail が変わるたびに開き直すと、1件開いた後に自分の更新で再取得が止まらない。
  useEffect(() => { if (initialBundleId) openDetail(initialBundleId); }, [initialBundleId]);
  const reloadDetail = () => detail && openDetail(detail.bundle.id);

  const reorder = async (idx, dir) => {
    const ids = detail.documents.map((d) => d.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try { await dmsApi.updateBundle(detail.bundle.id, { document_ids: ids }); reloadDetail(); }
    catch (e) { toast.error(e?.response?.data?.detail || '並び替えに失敗しました'); }
  };
  // 並べ替えはバインダーの並び順そのものを書き換える（▲▼も続けて使える）。
  const applySort = async (key, label) => {
    const dir = sort?.key === key && sort.dir === 'asc' ? 'desc' : 'asc';
    const ids = [...detail.documents].sort((a, b) => {
      const va = String(a[key] ?? ''); const vb = String(b[key] ?? '');
      const r = va.localeCompare(vb, 'ja', { numeric: true, sensitivity: 'base' });
      return dir === 'asc' ? r : -r;
    }).map((d) => d.id);
    setSort({ key, dir });
    try {
      await dmsApi.updateBundle(detail.bundle.id, { document_ids: ids });
      toast.success(`${label}で並べ替えました（${dir === 'asc' ? '昇順' : '降順'}）`);
      reloadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || '並べ替えに失敗しました'); }
  };
  const removeMember = async (d) => {    const ids = detail.documents.map((x) => x.id).filter((x) => x !== d.id);
    if (!ids.length) { toast.error('最後の1件は外せません（バインダーごと削除してください）'); return; }
    try { await dmsApi.updateBundle(detail.bundle.id, { document_ids: ids }); toast.success('バインダーから外しました'); reloadDetail(); loadList(); }
    catch (e) { toast.error(e?.response?.data?.detail || '操作に失敗しました'); }
  };
  const rename = async () => {
    if (!renaming?.name?.trim()) return;
    try {
      await dmsApi.updateBundle(detail.bundle.id, { name: renaming.name.trim() });
      toast.success('名前を変更しました'); setRenaming(null); reloadDetail(); loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || '変更に失敗しました'); }
  };
  const removeBundle = async (b) => {
    if (!window.confirm(`バインダー「${b.name}」を削除しますか？（中の書類は削除されません）`)) return;
    try { await dmsApi.deleteBundle(b.id); toast.success('バインダーを削除しました'); setDetail(null); loadList(); }
    catch (e) { toast.error(e?.response?.data?.detail || '削除に失敗しました'); }
  };
  const openAsSet = (docs, startIdx = 0) => {
    if (!docs.length) { toast.error('表示できる書類がありません'); return; }
    onOpenDoc(docs[startIdx].id, { name: detail.bundle.name, ids: docs.map((d) => d.id) });
  };

  if (detail) {
    const { bundle, documents } = detail;
    return (
      <div className="space-y-3" data-testid="bundle-detail">
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={() => { setDetail(null); loadList(); }} className="text-zinc-400 hover:text-zinc-700" data-testid="bundle-back">
            <CaretLeft size={18} />
          </button>
          <Books size={20} weight="duotone" className="text-amber-600" />
          <h2 className="text-sm font-bold text-zinc-800" data-testid="bundle-detail-name">{bundle.name}</h2>
          <span className="text-[10px] text-zinc-400">{documents.length}件 · {bundle.created_by}</span>
          <div className="ml-auto flex items-center gap-1.5">
            <Button size="sm" onClick={() => openAsSet(documents)} className="h-8 bg-amber-600 hover:bg-amber-700 text-xs" data-testid="bundle-open-set">
              <BookOpen size={14} className="mr-1" /> 綴じて開く
            </Button>
            {meta.can_edit && (
              <>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setExtShareOpen(true)} data-testid="bundle-share-btn">
                  <ShareNetwork size={13} className="mr-1" /> 共有
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setRenaming({ name: bundle.name })} data-testid="bundle-rename-btn">
                  <PencilSimple size={13} className="mr-1" /> 名前
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs text-rose-600 hover:bg-rose-50" onClick={() => removeBundle(bundle)} data-testid="bundle-delete-btn">
                  <Trash size={13} />
                </Button>
              </>
            )}
          </div>
        </div>
        {documents.length === 0 ? (
          <p className="text-xs text-zinc-400 py-10 text-center">表示できる書類がありません</p>
        ) : (
          <>
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="bundle-sortbar">
              <span className="text-[11px] text-zinc-400">並び替え</span>
              {meta.can_edit ? SORTS.map((s) => {
                const on = sort?.key === s.key;
                return (
                  <button key={s.key} type="button" onClick={() => applySort(s.key, s.label)}
                    className={`inline-flex items-center gap-0.5 rounded-full border px-2.5 py-0.5 text-[11px] transition-colors ${
                      on ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50'}`}
                    data-testid={`bundle-sort-${s.key}`}>
                    {s.label}
                    {on && (sort.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                  </button>
                );
              }) : (
                <span className="text-[11px] text-zinc-400">（編集権限のある人が並び順を変えられます）</span>
              )}
              <span className="ml-auto flex items-center gap-0.5 rounded-lg border border-zinc-200 bg-white p-0.5">
                {[['list', Rows, '一覧'], ['grid', SquaresFour, 'グリッド']].map(([k, IconC, lbl]) => (
                  <button key={k} type="button" onClick={() => setLayout(k)}
                    title={lbl}
                    className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] transition-colors ${
                      layout === k ? 'bg-amber-50 text-amber-800' : 'text-zinc-500 hover:bg-zinc-50'}`}
                    data-testid={`bundle-layout-${k}`}><IconC size={13} /> {lbl}</button>
                ))}
              </span>
            </div>
            {layout === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="bundle-grid">
                {documents.map((d, i) => (
                  <div key={d.id} className="rounded-lg border border-zinc-200 bg-white overflow-hidden"
                    data-testid={`bundle-member-${d.id}`}>
                    <button type="button" onClick={() => setBig(d)} title="クリックで拡大"
                      className="block w-full h-36 overflow-hidden cursor-zoom-in"
                      data-testid={`bundle-preview-${d.id}`}>
                      <MemberThumb docId={d.id} mime={d.mime_type} className="h-36 w-full" iconSize={28} />
                    </button>
                    <div className="p-2">
                      <p className="flex items-center gap-1 text-[10px] text-amber-600 font-black tabular-nums">
                        {i + 1}
                        <span className="font-mono font-normal text-zinc-400 truncate">{d.document_number}</span>
                      </p>
                      <button type="button" onClick={() => openAsSet(documents, i)}
                        className="mt-0.5 block w-full text-left text-[12px] font-medium text-zinc-800 line-clamp-2 hover:text-amber-700 hover:underline">
                        {d.title}
                      </button>
                      <p className="mt-1 text-[10px] text-zinc-400 truncate">{d.doc_type_label} · {fmtDate(d.updated_at)}</p>
                      {meta.can_edit && (
                        <span className="mt-1 flex items-center gap-1">
                          <button type="button" disabled={i === 0} onClick={() => reorder(i, -1)}
                            className="text-zinc-300 hover:text-zinc-600 disabled:opacity-30" data-testid={`bundle-up-${d.id}`}><ArrowUp size={13} /></button>
                          <button type="button" disabled={i === documents.length - 1} onClick={() => reorder(i, 1)}
                            className="text-zinc-300 hover:text-zinc-600 disabled:opacity-30" data-testid={`bundle-down-${d.id}`}><ArrowDown size={13} /></button>
                          <button type="button" onClick={() => removeMember(d)} title="バインダーから外す"
                            className="ml-auto text-zinc-300 hover:text-rose-500" data-testid={`bundle-remove-${d.id}`}><X size={13} /></button>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-1">
                {documents.map((d, i) => (
                  <div key={d.id} className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-2.5 py-2" data-testid={`bundle-member-${d.id}`}>
                    <span className="text-[10px] font-black text-amber-600 tabular-nums w-5 text-center shrink-0">{i + 1}</span>
                    <button type="button" onClick={() => setBig(d)} title="クリックで拡大"
                      className="h-11 w-9 shrink-0 overflow-hidden rounded border border-zinc-200 cursor-zoom-in"
                      data-testid={`bundle-preview-${d.id}`}>
                      <MemberThumb docId={d.id} mime={d.mime_type} className="h-11 w-9" iconSize={16} />
                    </button>
                    <button type="button" onClick={() => openAsSet(documents, i)} className="min-w-0 flex-1 text-left group">
                      <span className="block text-[13px] font-medium text-zinc-800 truncate group-hover:text-amber-700 group-hover:underline">{d.title}</span>
                      <span className="block text-[10px] text-zinc-400 font-mono">{d.document_number} · {d.doc_type_label} · {fmtSize(d.file_size)} · {fmtDate(d.updated_at)}</span>
                    </button>
                    <span className={`px-1.5 py-0.5 rounded border text-[9px] font-bold shrink-0 ${STATUS_STYLE[d.status] || ''}`}>{d.status_label}</span>
                    {meta.can_edit && (
                      <span className="flex items-center gap-0.5 shrink-0">
                        <button type="button" disabled={i === 0} onClick={() => reorder(i, -1)} className="text-zinc-300 hover:text-zinc-600 disabled:opacity-30" data-testid={`bundle-up-${d.id}`}><ArrowUp size={14} /></button>
                        <button type="button" disabled={i === documents.length - 1} onClick={() => reorder(i, 1)} className="text-zinc-300 hover:text-zinc-600 disabled:opacity-30" data-testid={`bundle-down-${d.id}`}><ArrowDown size={14} /></button>
                        <button type="button" onClick={() => removeMember(d)} className="text-zinc-300 hover:text-rose-500 ml-1" title="バインダーから外す" data-testid={`bundle-remove-${d.id}`}><X size={14} /></button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {big && (
          <DocFileViewer documentId={big.id} filename={big.current_filename || big.title}
            mime={big.mime_type} title={big.title} onClose={() => setBig(null)} />
        )}
        <ShareMenu open={extShareOpen} onClose={() => setExtShareOpen(false)}
          bundleId={detail?.bundle?.id} label={detail?.bundle?.name} isAdmin={!!meta?.is_admin} />
        <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
          <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="bundle-rename-dialog">
            <DialogHeader><DialogTitle className="text-base">バインダー名を変更</DialogTitle></DialogHeader>
            <Input value={renaming?.name || ''} onChange={(e) => setRenaming({ name: e.target.value })}
              className="h-9 text-sm" data-testid="bundle-rename-input" onKeyDown={(e) => e.key === 'Enter' && rename()} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenaming(null)}>キャンセル</Button>
              <Button onClick={rename} className="bg-amber-600 hover:bg-amber-700" data-testid="bundle-rename-save">保存</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="bundles-view">
      <div className="flex items-center gap-2">
        <Books size={20} weight="duotone" className="text-amber-600" />
        <h2 className="text-sm font-bold text-zinc-800">バインダー</h2>
        <span className="text-[10px] text-zinc-400">{bundles.length}冊</span>
        {meta.can_edit && (
          <Button size="sm" variant="outline" onClick={() => setGroupOpen(true)}
            className="ml-auto h-8 text-xs border-teal-600/40 text-teal-800 hover:bg-teal-50"
            data-testid="auto-group-btn">
            <Sparkle size={14} weight="duotone" className="mr-1 text-teal-700" /> AIでまとめる
          </Button>
        )}
        {meta.can_edit && (
          <Button size="sm" onClick={() => setPickOpen(true)}
            className="h-8 bg-amber-600 hover:bg-amber-700 text-xs"
            data-testid="bundle-create-btn">
            <Books size={14} className="mr-1" /> バインダーを作成
          </Button>
        )}
      </div>
      <p className="text-[11px] text-zinc-400">
        「AIでまとめる」は書類の索引を読んで綴りの候補を出します（作るのは選んだ分だけ）。
        自分で選ぶなら「バインダーを作成」。まとめてスキャンしたPDFを分割した時は、
        同じ図番の幹ごとに自動で1冊できます。
      </p>
      {bundles.length === 0 ? (
        <div className="py-14 text-center text-zinc-400" data-testid="bundles-empty">
          <Books size={40} weight="duotone" className="mx-auto mb-2" />
          <p className="text-sm">バインダーはまだありません</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {bundles.map((b) => (
            <button key={b.id} type="button" onClick={() => openDetail(b.id)}
              className="text-left rounded-xl border-l-4 border border-amber-300 border-l-amber-500 bg-white p-3 shadow-sm hover:shadow-md transition-all"
              data-testid={`bundle-card-${b.id}`}>
              <span className="flex items-center gap-1.5 mb-1.5">
                <Books size={20} weight="duotone" className="text-amber-600" />
                {b.auto_created && (
                  <span className="rounded bg-teal-50 border border-teal-200 px-1.5 py-0.5 text-[9px] font-bold text-teal-700"
                    data-testid={`bundle-auto-badge-${b.id}`}>自動</span>
                )}
              </span>
              <p className="text-xs font-bold text-zinc-800 line-clamp-2">{b.name}</p>
              <p className="text-[10px] text-zinc-400 mt-1">
                {b.member_count}件 · {fmtDate(b.updated_at)}
                {b.trashed_count > 0 && (
                  <span className="text-rose-600" data-testid={`dms-bundle-trashed-${b.id}`}>
                    {' '}· {b.trashed_count}件はゴミ箱
                  </span>
                )}
                {b.hidden_count > 0 && (
                  <span className="text-zinc-400"> · 他{b.hidden_count}件は権限により非表示</span>
                )}
              </p>
              {(b.tags || []).length > 0 && (
                <span className="mt-1 flex flex-wrap gap-1" data-testid={`bundle-tags-${b.id}`}>
                  {b.tags.slice(0, 4).map((t) => (
                    <span key={t} className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[9px] text-zinc-600">{t}</span>
                  ))}
                </span>
              )}
              {(b.preview_titles || []).length > 0 && (
                <p className="text-[9px] text-zinc-400 mt-1 line-clamp-2">{b.preview_titles.join(' / ')}</p>
              )}
            </button>
          ))}
        </div>
      )}
      <PickDocumentsDialog open={pickOpen} onClose={() => setPickOpen(false)}
        onCreated={() => { setPickOpen(false); loadList(); }} />
      <AutoGroupDialog open={groupOpen} folders={folders} onClose={() => setGroupOpen(false)}
        onCreated={() => { setGroupOpen(false); loadList(); }} />
    </div>
  );
}
